import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import { AI_MENTOR } from '../lib/mentorIdentity';
import { chatApi, authApi } from '../services/api';
import { resolvePortalAccessUrl } from '../lib/portalAccessUrl';
import DesktopAppIconModal from '../components/DesktopAppIconModal';
import {
  DESKTOP_ICON_MENU_LABEL,
  PHONE_QR_MENU_LABEL,
  PHONE_QR_MODAL_BODY,
  PHONE_QR_MODAL_TITLE,
} from '../lib/desktopAppIcon';
import { sanitizeAssistantMessages, sanitizeMentorOutput } from '../lib/sanitizeMentorOutput';
import { resolveMentorFromBio } from '../lib/voiceProfile';
import {
  cancelSpeechPlayback,
  prepareSpeechAudio,
  resetAudioPlaybackGate,
  speakMentorReply,
  unlockSpeechPlayback,
  SPOKEN_TEXT_ATTRS,
} from '../lib/speechPlayback';
import { resolveWorkspaceScreenId, MENTEE_KEY_STORAGE, normalizeClientKey } from '../lib/screenRegistry';
import { BRIEFING_ROOM_WELCOME } from '../components/LivingRoomConsole';
import './Workspace.css';

const LOGO = encodeURI('/Assets/logo/Mentology Logo.png');
const DEFAULT_MENTOR_VOICE_ID = 's116Gqp9aiAQAe29Albd';

interface Message {
  role: string;
  content: string;
  proteusMode?: string;
}

interface Bio {
  preferred_name?: string;
  name?: string;
  language_target?: string;
  ai_mentor_gender?: string;
  ai_mentor_name?: string;
  ai_mentor_voice_id?: string;
}

function buildBriefingScript(menteeName: string, mentorName: string) {
  return BRIEFING_ROOM_WELCOME
    .replace(/\[Mentee(?:'s)? Name\]/gi, menteeName)
    .replace(/\[Mentor Name\]/gi, mentorName);
}

function splitSentences(script: string): string[] {
  const matches = script.match(/[^.?!]+[.?!]+(?:\s+|$)|[^.?!]+$/g);
  if (!matches?.length) return [script.trim()].filter(Boolean);
  return matches.map((part) => part.trim()).filter(Boolean);
}

function estimateSpeechDurationMs(script: string) {
  const words = script.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1200, (words / 150) * 60 * 1000);
}

function waitForAudioEnded(audio: HTMLAudioElement): Promise<void> {
  if (audio.ended) return Promise.resolve();
  return new Promise((resolve) => {
    const finish = () => resolve();
    audio.addEventListener('ended', finish, { once: true });
    audio.addEventListener('error', finish, { once: true });
  });
}

function resolveMenteeName(bio: Bio | null, _userName?: string) {
  // Greeting uses Preferred Name only (Setup field).
  const fromBio = bio?.preferred_name?.trim();
  if (fromBio) return fromBio;

  try {
    const pending = sessionStorage.getItem('mentology.pendingBio');
    if (pending) {
      const parsed = JSON.parse(pending) as Bio;
      const fromPending = parsed.preferred_name?.trim();
      if (fromPending) return fromPending;
    }
  } catch {
    // ignore
  }

  try {
    const stored = localStorage.getItem('mentology.setupDisplayName')?.trim();
    if (stored) return stored;
  } catch {
    // ignore
  }

  return 'your mentee';
}

function computeSubscriptionDaysLeft() {
  return 28;
}

function readPendingBio(): Bio | null {
  try {
    const pendingRaw = sessionStorage.getItem('mentology.pendingBio');
    return pendingRaw ? JSON.parse(pendingRaw) : null;
  } catch {
    return null;
  }
}

function resolveActiveClientKey(userToken?: string) {
  const stored = sessionStorage.getItem(MENTEE_KEY_STORAGE);
  const candidate = stored || userToken || '';
  return normalizeClientKey(candidate);
}

function isEltMenteeTrack(userToken?: string) {
  return resolveActiveClientKey(userToken).startsWith('ELT-');
}

export default function Workspace() {
  const navigate = useNavigate();
  const { user, chatEnabled, setChatEnabled, logout } = useAuth();
  const workspaceScreenId = resolveWorkspaceScreenId(user?.role);
  const isEltTrack = isEltMenteeTrack(user?.userToken);
  const activeClientKey = resolveActiveClientKey(user?.userToken);
  const [bio, setBio] = useState<Bio | null>(readPendingBio);
  const assignedAiMentorName = resolveMentorFromBio(bio).name;
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [desktopIconOpen, setDesktopIconOpen] = useState(false);
  const [introDisplay, setIntroDisplay] = useState('');
  const [introComplete, setIntroComplete] = useState(false);
  const [localChatEnabled, setLocalChatEnabled] = useState(false);
  const [devBypassActive, setDevBypassActive] = useState(false);
  const [listening, setListening] = useState(false);
  const [consoleFrozen, setConsoleFrozen] = useState(false);
  const [freezeMessage, setFreezeMessage] = useState('');
  const [reflection, setReflection] = useState('');
  const [synthesis, setSynthesis] = useState<{
    required: boolean;
    report?: { cycle_number: number; report_json: string; completed: number };
    showBilling?: boolean;
  } | null>(null);

  const playbackStarted = useRef(false);
  const bioRef = useRef(bio);
  const userRef = useRef(user);
  bioRef.current = bio;
  userRef.current = user;
  const introScriptRef = useRef('');
  const introRunIdRef = useRef(0);
  const introAudioRef = useRef<HTMLAudioElement | null>(null);
  const pendingAudioRef = useRef<HTMLAudioElement | null>(null);
  const syncRafRef = useRef<number | null>(null);
  const devBypassRef = useRef(false);
  const recognitionRef = useRef<{ stop: () => void; start: () => void } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const aiTextRef = useRef<HTMLDivElement>(null);

  const subscriptionId = user?.userToken || 'PRO-XXXX';
  const subscriptionDaysLeft = computeSubscriptionDaysLeft();

  const stopSyncAnimation = useCallback(() => {
    if (syncRafRef.current != null) {
      cancelAnimationFrame(syncRafRef.current);
      syncRafRef.current = null;
    }
  }, []);

  const releaseIntroAudio = useCallback(() => {
    if (introAudioRef.current) {
      introAudioRef.current.pause();
      introAudioRef.current = null;
    }
    if (pendingAudioRef.current) {
      pendingAudioRef.current.pause();
      pendingAudioRef.current = null;
    }
  }, []);

  const invalidateIntroRun = useCallback(() => {
    introRunIdRef.current += 1;
    stopSyncAnimation();
    releaseIntroAudio();
    cancelSpeechPlayback();
    playbackStarted.current = false;
  }, [releaseIntroAudio, stopSyncAnimation]);

  const finishIntro = useCallback((script: string) => {
    stopSyncAnimation();
    setIntroDisplay(script);
    setIntroComplete(true);
  }, [stopSyncAnimation]);

  const syncChunkTypewriter = useCallback((
    fullScript: string,
    chunkText: string,
    chunkStart: number,
    audio: HTMLAudioElement,
  ) => {
    const tick = () => {
      const durationSec = audio.duration && Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration
        : estimateSpeechDurationMs(chunkText) / 1000;
      const progress = Math.min(1, (audio.currentTime || 0) / Math.max(durationSec, 0.01));
      const chunkChars = Math.max(1, Math.ceil(progress * chunkText.length));
      setIntroDisplay(fullScript.slice(0, chunkStart + chunkChars));

      if (progress < 1 && !audio.ended) {
        syncRafRef.current = requestAnimationFrame(tick);
        return;
      }

      setIntroDisplay(fullScript.slice(0, chunkStart + chunkText.length));
      syncRafRef.current = null;
    };

    stopSyncAnimation();
    syncRafRef.current = requestAnimationFrame(tick);
  }, [stopSyncAnimation]);

  const typeChunkEstimated = useCallback((
    fullScript: string,
    chunkText: string,
    chunkStart: number,
    isActive: () => boolean,
  ) => new Promise<void>((resolve) => {
    const durationMs = estimateSpeechDurationMs(chunkText);
    const startedAt = performance.now();

    const tick = () => {
      if (!isActive()) {
        resolve();
        return;
      }

      const progress = Math.min(1, (performance.now() - startedAt) / durationMs);
      const chunkChars = Math.max(1, Math.ceil(progress * chunkText.length));
      setIntroDisplay(fullScript.slice(0, chunkStart + chunkChars));

      if (progress < 1) {
        syncRafRef.current = requestAnimationFrame(tick);
        return;
      }

      setIntroDisplay(fullScript.slice(0, chunkStart + chunkText.length));
      syncRafRef.current = null;
      resolve();
    };

    stopSyncAnimation();
    syncRafRef.current = requestAnimationFrame(tick);
  }), [stopSyncAnimation]);

  const startIntroPlayback = useCallback((
    script: string,
    voiceId?: string | null,
    modelId?: string | null,
    languageTarget?: string | null,
  ) => {
    if (playbackStarted.current) return;
    playbackStarted.current = true;

    const runId = ++introRunIdRef.current;
    const isActive = () => introRunIdRef.current === runId && !devBypassRef.current;
    const resolvedVoiceId = voiceId || DEFAULT_MENTOR_VOICE_ID;
    const resolvedLanguage = languageTarget ?? bioRef.current?.language_target ?? undefined;
    const sentences = splitSentences(script);

    introScriptRef.current = script;
    setIntroDisplay('');
    setIntroComplete(false);
    resetAudioPlaybackGate();
    stopSyncAnimation();

    const prepareChunk = (text: string) =>
      prepareSpeechAudio(resolvedVoiceId, text, resolvedLanguage, modelId ?? undefined, { cancelActive: false })
        .catch(() => null);

    void (async () => {
      let charOffset = 0;
      let nextAudioPromise: Promise<HTMLAudioElement | null> | null = sentences[0]
        ? prepareChunk(sentences[0])
        : null;

      for (let index = 0; index < sentences.length; index += 1) {
        if (!isActive()) return;

        const chunk = sentences[index];
        const audio = nextAudioPromise
          ? await nextAudioPromise
          : await prepareChunk(chunk);
        nextAudioPromise = null;

        if (!isActive()) {
          audio?.pause();
          return;
        }

        const foundAt = script.indexOf(chunk, charOffset);
        const chunkStart = foundAt >= 0 ? foundAt : charOffset;

        if (audio) {
          pendingAudioRef.current = null;
          introAudioRef.current = audio;

          if (index + 1 < sentences.length) {
            nextAudioPromise = prepareChunk(sentences[index + 1]).then((nextAudio) => {
              if (isActive()) pendingAudioRef.current = nextAudio;
              else nextAudio?.pause();
              return nextAudio;
            });
          }

          try {
            await audio.play();
            if (!isActive()) {
              audio.pause();
              return;
            }
            syncChunkTypewriter(script, chunk, chunkStart, audio);
            await waitForAudioEnded(audio);
          } catch (error) {
            console.warn('[Briefing chunk play]', error);
            if (!isActive()) return;
            await typeChunkEstimated(script, chunk, chunkStart, isActive);
          }
        } else {
          await typeChunkEstimated(script, chunk, chunkStart, isActive);
        }

        if (!isActive()) return;

        charOffset = chunkStart + chunk.length;
        while (charOffset < script.length && /\s/.test(script[charOffset])) {
          charOffset += 1;
        }
        setIntroDisplay(script.slice(0, charOffset));
      }

      if (isActive()) finishIntro(script);
    })().catch((error) => {
      console.warn('[Briefing intro]', error);
      if (isActive()) finishIntro(script);
    });
  }, [finishIntro, stopSyncAnimation, syncChunkTypewriter, typeChunkEstimated]);

  useEffect(() => {
    if (aiTextRef.current) {
      aiTextRef.current.scrollTop = aiTextRef.current.scrollHeight;
    }
  }, [introDisplay]);

  useEffect(() => {
    resetAudioPlaybackGate();
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
      introRunIdRef.current += 1;
      if (syncRafRef.current != null) {
        cancelAnimationFrame(syncRafRef.current);
        syncRafRef.current = null;
      }
      if (introAudioRef.current) {
        introAudioRef.current.pause();
        introAudioRef.current = null;
      }
      if (pendingAudioRef.current) {
        pendingAudioRef.current.pause();
        pendingAudioRef.current = null;
      }
      cancelSpeechPlayback();
      playbackStarted.current = false;
    };
  }, []);

  useEffect(() => {
    const onDevBypass = (event: KeyboardEvent) => {
      if (!event.ctrlKey || !event.shiftKey || event.key.toLowerCase() !== 'b') return;
      event.preventDefault();

      invalidateIntroRun();

      const menteeName = resolveMenteeName(bioRef.current, userRef.current?.name);
      const mentorProfile = resolveMentorFromBio(bioRef.current);
      const script = buildBriefingScript(menteeName, mentorProfile.name);

      devBypassRef.current = true;
      playbackStarted.current = true;
      introScriptRef.current = script;

      resetAudioPlaybackGate();
      setIntroDisplay(script);
      setIntroComplete(true);
      setLocalChatEnabled(true);
      setChatEnabled?.(true);
      setDevBypassActive(true);
    };

    window.addEventListener('keydown', onDevBypass);
    return () => window.removeEventListener('keydown', onDevBypass);
  }, [invalidateIntroRun, setChatEnabled]);

  useEffect(() => {
    authApi.me()
      .then((data) => {
        if (data?.gate?.route === '/elt-setup' && data.gate.reason === 'setup_required') {
          navigate('/elt-setup', { replace: true });
        }
      })
      .catch(() => {});
  }, [navigate]);

  useEffect(() => {
    authApi.getBio()
      .then((data) => {
        setBio((current) => ({ ...(current || {}), ...(data.bio || {}) }));
        if (data.bio) {
          sessionStorage.removeItem('mentology.pendingBio');
        }
      })
      .catch(() => {});
    chatApi.getHistory().then((d) => {
      const history = d.messages || [];
      if (history.length > 0) {
        setMessages(sanitizeAssistantMessages(history));
        setIntroComplete(true);
      }
    }).catch(() => {});
    chatApi.synthesisStatus().then(setSynthesis).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || devBypassActive || devBypassRef.current) return;

    const menteeName = resolveMenteeName(bio, user?.name);
    const mentorProfile = resolveMentorFromBio(bio);
    const script = buildBriefingScript(menteeName, mentorProfile.name);

    if (introComplete || messages.length > 0) {
      if (playbackStarted.current || introAudioRef.current) {
        invalidateIntroRun();
      }
      setIntroDisplay(script);
      return;
    }

    if (playbackStarted.current && introScriptRef.current !== script) {
      invalidateIntroRun();
    }

    if (playbackStarted.current) return;

    startIntroPlayback(script, mentorProfile.voiceId, mentorProfile.modelId, bio?.language_target ?? null);
  }, [bio, user, introComplete, messages.length, startIntroPlayback, devBypassActive, invalidateIntroRun]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const sendMessage = async () => {
    if (!input.trim() || loading || !canSend) return;
    unlockSpeechPlayback();
    releaseIntroAudio();
    cancelSpeechPlayback();
    const userMsg = input.trim();
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const result = await chatApi.sendMessage(userMsg);
      if (result.crisis || result.workspaceLocked) {
        const lockedText = sanitizeMentorOutput(result.message || result.injectedMessage || '');
        setConsoleFrozen(true);
        setFreezeMessage(lockedText);
        setMessages((m) => [...m, { role: 'system', content: lockedText }]);
        return;
      }
      const spoken = sanitizeMentorOutput(result.message.content);
      setMessages((m) => [...m, {
        role: 'assistant',
        content: spoken,
        proteusMode: result.message.proteusMode,
      }]);
      const mentorProfile = resolveMentorFromBio(bioRef.current);
      void speakMentorReply(
        spoken,
        mentorProfile.voiceId || DEFAULT_MENTOR_VOICE_ID,
        bioRef.current?.language_target,
      ).catch((error) => {
        console.warn('[Briefing reply voice]', error);
      });
    } catch (err: unknown) {
      const e = err as { error?: string };
      setMessages((m) => [...m, { role: 'system', content: e.error || 'Message failed' }]);
    } finally {
      setLoading(false);
    }
  };

  const toggleMic = () => {
    const SpeechRecognitionCtor = (window as Window & {
      SpeechRecognition?: new () => {
        continuous: boolean;
        interimResults: boolean;
        lang: string;
        start: () => void;
        stop: () => void;
        onresult: ((event: { results: ArrayLike<{ 0: { transcript: string } }> }) => void) | null;
        onend: (() => void) | null;
        onerror: (() => void) | null;
      };
      webkitSpeechRecognition?: new () => {
        continuous: boolean;
        interimResults: boolean;
        lang: string;
        start: () => void;
        stop: () => void;
        onresult: ((event: { results: ArrayLike<{ 0: { transcript: string } }> }) => void) | null;
        onend: (() => void) | null;
        onerror: (() => void) | null;
      };
    }).SpeechRecognition || (window as Window & { webkitSpeechRecognition?: new () => {
      continuous: boolean;
      interimResults: boolean;
      lang: string;
      start: () => void;
      stop: () => void;
      onresult: ((event: { results: ArrayLike<{ 0: { transcript: string } }> }) => void) | null;
      onend: (() => void) | null;
      onerror: (() => void) | null;
    } }).webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) {
      alert('Voice input is not supported in this browser.');
      return;
    }

    if (listening && recognitionRef.current) {
      recognitionRef.current.stop();
      setListening(false);
      return;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-AU';

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join('');
      setInput(transcript);
    };

    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const handleCreateAppIcon = () => {
    setMenuOpen(false);
    setDesktopIconOpen(true);
  };

  const handleShowQrCode = () => {
    setMenuOpen(false);
    setQrModalOpen(true);
  };

  const handleSignOut = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/', { replace: true });
  };

  const contactMentorHref = 'mailto:support@mentology.com.au?subject=Contact%20Assigned%20Mentor';

  const submitReflection = async () => {
    if (!synthesis?.report) return;
    await chatApi.submitReflection(synthesis.report.cycle_number, reflection);
    setSynthesis(null);
    chatApi.synthesisStatus().then(setSynthesis);
  };

  const canSend = !consoleFrozen && (devBypassActive || (introComplete && (chatEnabled || localChatEnabled)));

  if (synthesis?.required && synthesis.report && !synthesis.report.completed) {
    const report = JSON.parse(synthesis.report.report_json);
    return (
      <div className="workspace-screen" data-screen-id={workspaceScreenId}>
        <div className="workspace-util" ref={menuRef}>
          <button type="button" className="workspace-util-trigger" onClick={() => setMenuOpen((o) => !o)}>
            Menu
          </button>
        </div>
        <div className="workspace-logo-wrap">
          <img src={LOGO} alt="Mentology" className="workspace-logo brand-logo" />
        </div>
        <div className="workspace-reflection">
          <h2 className="workspace-reflection-title">Day 28 Synthesis</h2>
          <div className="workspace-bubble workspace-bubble-assistant scannable-bullet-text">
            {report.actionLearningLoops?.map((loop: { phase: string; summary: string }) => (
              <p key={loop.phase}><strong>{loop.phase}:</strong> {loop.summary}</p>
            ))}
          </div>
          <textarea
            className="input-field workspace-reflection-input"
            placeholder="Your evaluation response..."
            value={reflection}
            onChange={(e) => setReflection(e.target.value)}
          />
          <button type="button" className="btn-primary workspace-reflection-btn" onClick={submitReflection}>
            Submit Reflection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`workspace-screen${consoleFrozen ? ' workspace-locked' : ''}`} data-screen-id={workspaceScreenId}>
      <div className="workspace-util" ref={menuRef}>
        <button
          type="button"
          className="workspace-util-trigger"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
        >
          Menu
        </button>
        {menuOpen && (
          <div className="workspace-util-dropdown">
            {isEltTrack ? (
              <>
                <p className="workspace-util-item workspace-util-static">
                  Key: {activeClientKey}
                </p>
                <p className="workspace-util-item workspace-util-static">
                  {AI_MENTOR}
                </p>
                {assignedAiMentorName && (
                  <p className="workspace-util-item workspace-util-static">
                    Voice: {assignedAiMentorName}
                  </p>
                )}
                <hr className="workspace-util-divider" />
                <button
                  type="button"
                  className="workspace-util-item"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/elt-setup');
                  }}
                >
                  Change setup
                </button>
                <button
                  type="button"
                  className="workspace-util-item"
                  onClick={handleCreateAppIcon}
                >
                  {DESKTOP_ICON_MENU_LABEL}
                </button>
                <button
                  type="button"
                  className="workspace-util-item"
                  onClick={handleShowQrCode}
                >
                  {PHONE_QR_MENU_LABEL}
                </button>
                <hr className="workspace-util-divider" />
                <a
                  className="workspace-util-item"
                  href={contactMentorHref}
                  onClick={() => setMenuOpen(false)}
                >
                  Contact Mentor
                </a>
                <button
                  type="button"
                  className="workspace-util-item"
                  onClick={handleSignOut}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <p className="workspace-util-item workspace-util-static">
                  Subscription: {subscriptionId}
                </p>
                <p className="workspace-util-item workspace-util-static">
                  Subscription days left: {subscriptionDaysLeft}
                </p>
                <button
                  type="button"
                  className="workspace-util-item"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/pro-setup');
                  }}
                >
                  Review Setup
                </button>
                <button
                  type="button"
                  className="workspace-util-item"
                  onClick={handleCreateAppIcon}
                >
                  {DESKTOP_ICON_MENU_LABEL}
                </button>
                <button
                  type="button"
                  className="workspace-util-item"
                  onClick={handleShowQrCode}
                >
                  {PHONE_QR_MENU_LABEL}
                </button>
                <button type="button" className="workspace-util-item" onClick={() => setMenuOpen(false)}>
                  Renew subscription
                </button>
                <a
                  className="workspace-util-item"
                  href="mailto:support@mentology.com.au"
                  onClick={() => setMenuOpen(false)}
                >
                  Contact technical support
                </a>
              </>
            )}
          </div>
        )}
      </div>

      <div className="workspace-logo-wrap">
        <img
          src={LOGO}
          alt="Mentology"
          className="workspace-logo brand-logo"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      </div>

      <div className="workspace-body">
        <div className={`workspace-ai-box ${SPOKEN_TEXT_ATTRS.className}`}>
          <div
            ref={aiTextRef}
            className={`workspace-ai-text scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}
          >
            {introDisplay || (!introComplete && !playbackStarted.current ? ' ' : '')}
            {!introComplete && <span className="workspace-cursor">|</span>}
          </div>
        </div>

        <div className="workspace-thread">
          {messages.map((m, i) => (
            <div
              key={i}
              className={`workspace-bubble workspace-bubble-${m.role} scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}
            >
              {m.content}
            </div>
          ))}
          {loading && <p className="workspace-bubble workspace-bubble-assistant scannable-bullet-text">Processing...</p>}
        </div>

        {consoleFrozen && (
          <div className="workspace-freeze-banner" role="alert">
            <p className="workspace-freeze-title">Chat workspace locked</p>
            <p>{freezeMessage}</p>
          </div>
        )}

        <form
          className="workspace-input-wrap"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSend && input.trim() && !loading) sendMessage();
          }}
        >
          <textarea
            className="workspace-chat-input"
            placeholder={consoleFrozen ? 'Chat workspace locked' : 'Share your challenge and goals...'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (canSend && input.trim() && !loading) sendMessage();
              }
            }}
            disabled={loading || consoleFrozen}
            aria-label="Mentee message input"
          />
          <div className="workspace-input-controls">
            <button
              type="submit"
              className="workspace-send-btn"
              disabled={loading || !canSend || !input.trim()}
            >
              Send
            </button>
            <button
              type="button"
              className={`workspace-mic-btn ${listening ? 'workspace-mic-active' : ''}`}
              onClick={toggleMic}
              disabled={loading || consoleFrozen}
              aria-label="Microphone"
              title="Microphone"
            >
              🎙️
            </button>
          </div>
        </form>
      </div>

      {qrModalOpen && (
        <div
          className="workspace-qr-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="workspace-qr-title"
          onClick={() => setQrModalOpen(false)}
        >
          <div
            className="workspace-qr-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="workspace-qr-title" className="section-header">{PHONE_QR_MODAL_TITLE}</h2>
            <p className="workspace-qr-url scannable-bullet-text">{PHONE_QR_MODAL_BODY}</p>
            <p className="workspace-qr-url scannable-bullet-text">{resolvePortalAccessUrl()}</p>
            <div className="workspace-qr-code-wrap" aria-hidden="true">
              <QRCodeSVG
                value={resolvePortalAccessUrl()}
                size={220}
                level="M"
                bgColor={"#ffffff" /* qr-quiet-zone */}
                fgColor="#000000"
                includeMargin
              />
            </div>
            <button type="button" className="btn-primary workspace-qr-close" onClick={() => setQrModalOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}
      {desktopIconOpen && (
        <DesktopAppIconModal
          titleId="workspace-desktop-icon-title"
          onClose={() => setDesktopIconOpen(false)}
        />
      )}
    </div>
  );
}
